import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Save, Settings } from '@/lib/icons'
import type { RateTier, SettingsResponse, SettingsUpdateRequest } from '@ayub/shared'
import { formatNumber } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ToastViewport, useToast } from '@/components/ui/toast'
import { isInsideTelegram } from '@/lib/telegram'
import { updateSettingsRequest } from './api'
import { AdminsSection } from './AdminsSection'
import { LogoutSection } from './LogoutSection'
import { BloggerRulesSection } from './BloggerRulesSection'
import { CheckScheduleSection } from './CheckScheduleSection'
import { SettingsErrorBanner, SettingsRetryState } from './ErrorStates'
import { invalidateSettingsRelatedCaches, useAdmins, useCurrentAdmin, useSettings } from './hooks'
import { PayoutSection } from './PayoutSection'
import type { TierRow } from './PayoutSection'
import { formatIntegerInput } from './numericInput'
import {
  evaluateField,
  evaluateRateTiers,
  validateAutoPayoutThreshold,
  validateFirstCheckDays,
  validateLinkWaitDays,
  validateMinFollowers,
  validateReapplyAfterDays,
  validateScheduledPayoutDay,
} from './validation'
import { ViewsSourceSection } from './ViewsSourceSection'

/**
 * Formada tutiladigan xom (matn) qiymatlar. `rateTiers` bu yerda EMAS — u massiv bo'lgani
 * uchun alohida holatda boshqariladi (`tiers` state, pastda).
 */
interface Draft {
  autoPayoutThreshold: string
  scheduledPayoutDay: string
  firstCheckDays: string
  minFollowers: string
  reapplyAfterDays: string
  linkWaitDays: string
  viewsProvider: string
}

/**
 * `formatNumber()` bilan ming ajratkichi qo'shib ko'rsatiladi ("200 000") — `NumberField`
 * qabul qiladigan `parseIntegerInput()` bo'shliqni ming ajratkichi deb hisoblab qayta
 * o'qiydi (`numericInput.ts` izohi), shuning uchun formatlash raund-trip xavfsiz.
 */
function toDraft(settings: SettingsResponse): Draft {
  return {
    autoPayoutThreshold: formatNumber(settings.autoPayoutThreshold),
    scheduledPayoutDay: formatNumber(settings.scheduledPayoutDay),
    firstCheckDays: formatNumber(settings.firstCheckDays),
    minFollowers: formatNumber(settings.minFollowers),
    reapplyAfterDays: formatNumber(settings.reapplyAfterDays),
    linkWaitDays: formatNumber(settings.linkWaitDays),
    viewsProvider: settings.viewsProvider,
  }
}

/** Server bosqichlarini muharrir qatorlariga (formatlangan matn) o'giradi. */
function toTierRows(tiers: RateTier[]): TierRow[] {
  return tiers.map((t) => ({
    views: formatIntegerInput(String(t.views)),
    ratePer1000: formatIntegerInput(String(t.ratePer1000)),
  }))
}

/**
 * Oxirgi bosqich enini 0 ga tenglaydi ("qolgan barchasi" — eni e'tiborsiz). Solishtirishda
 * "fantom o'zgargan" holatini oldini oladi: muharrir har doim oxirgi enini 0 qiladi, server
 * esa oldingi qiymatni saqlab qolgan bo'lishi mumkin.
 */
function normalizeTiers(tiers: RateTier[]): RateTier[] {
  return tiers.map((t, i) =>
    i === tiers.length - 1 ? { views: 0, ratePer1000: t.ratePer1000 } : t,
  )
}

/**
 * Sahifa 6 — Sozlamalar (`docs/03-app-flow.md`, Bosqich 11). Props'siz ishlaydi —
 * `AppShell.tsx` uni to'g'ridan-to'g'ri navigatsiyaga ulaydi.
 *
 * Super admin aniqlash: `hooks.ts`dagi `useCurrentAdmin()` izohiga qarang — `GET
 * /api/auth/me` javobidagi `isSuper` to'g'ridan-to'g'ri o'qiladi. Bu birinchi himoya
 * qatlami (UI'da tugmalarni yashirish/o'chirish); ikkinchisi — server 403 qaytarsa, xato
 * matni to'g'ridan-to'g'ri `updateMutation.error` orqali pastda ko'rsatiladi (qaysi yo'l
 * bilan noto'g'ri aniqlansa ham, admin baribir tushunarli xabar ko'radi, jimgina hech
 * narsa bo'lmagandek qolmaydi).
 */
export function SettingsPage() {
  const queryClient = useQueryClient()
  const { toast, showToast } = useToast()

  const settings = useSettings()
  const admins = useAdmins()
  const currentAdmin = useCurrentAdmin()

  const [draft, setDraft] = useState<Draft | null>(null)
  // Bosqichli stavka — massiv bo'lgani uchun string-Draft'dan alohida holatda.
  const [tiers, setTiers] = useState<TierRow[] | null>(null)
  const tiersTouchedRef = useRef(false)

  // Foydalanuvchi HAQIQATDA qo'l tekkizgan maydonlar to'plami — pastdagi effekt shu
  // ro'yxatga qarab qaror qiladi (`bind()` va `viewsProvider.onChange` shu yerga yozadi).
  //
  // NEGA state emas, ref: bu to'plam hech qachon to'g'ridan-to'g'ri render qilinmaydi
  // (ekranda ko'rsatilmaydi) — faqat effekt ichida O'QILADI, shuning uchun uni
  // o'zgartirish qayta render talab qilmaydi. Agar `useState` bo'lganda, har bosilgan
  // harfda ORTIQCHA bitta re-render qo'shilardi (`draft`ning o'zi allaqachon shu ishni
  // qilyapti).
  const touchedRef = useRef<Set<keyof Draft>>(new Set())

  // ESKI YECHIM: draft faqat BIR MARTA (`draft === null` bo'lganda) to'ldirilardi — fondagi
  // qayta so'rov (masalan boshqa admin sozlamani o'zgartirganda) tegilmagan maydonlarni
  // ESKI qiymatda qoldirardi, natijada ular "o'zgargan" (`patchBody`ga tushadigan) bo'lib
  // qolardi va Saqlash bosilganda o'sha eski qiymat boshqa adminning yangi qiymatini jim
  // ustidan yozib yuborardi (fantom dirty).
  //
  // ENDI: server ma'lumoti YANGILANGANDA (`settings.data` REFERENSI o'zgarganda — TanStack
  // Query struktura bo'yicha solishtiradi, mazmuni bir xil bo'lsa referens o'zgarmaydi,
  // shuning uchun bu effekt ortiqcha ishlamaydi) faqat `touchedRef`da YO'Q (foydalanuvchi
  // tegmagan) maydonlar yangi server qiymati bilan sinxronlanadi; tegilgan (dirty)
  // maydonlar o'zining joriy (foydalanuvchi kiritgan) qiymatida qoladi.
  useEffect(() => {
    const data = settings.data
    if (!data) return
    setDraft((prev) => {
      const fresh = toDraft(data)
      if (prev === null) return fresh
      const merged = { ...fresh }
      for (const key of touchedRef.current) {
        merged[key] = prev[key]
      }
      return merged
    })
    // Bosqichlar: tegilmagan bo'lsa (yoki birinchi yuklash) server qiymati bilan sinxron.
    setTiers((prev) =>
      prev !== null && tiersTouchedRef.current ? prev : toTierRows(data.rateTiers),
    )
  }, [settings.data])

  const updateMutation = useMutation<SettingsResponse, Error, SettingsUpdateRequest>({
    mutationFn: (body) => updateSettingsRequest(body),
    onSuccess: (data) => {
      invalidateSettingsRelatedCaches(queryClient)
      // Saqlangan qiymatlar endi serverdagi bilan bir xil — "tegilgan" holat tozalanadi,
      // aks holda keyingi fon yangilanishi shu maydonlarni haqsiz ravishda eski (allaqachon
      // saqlangan) qiymatda muzlatib qo'yardi.
      touchedRef.current.clear()
      tiersTouchedRef.current = false
      setDraft(toDraft(data))
      setTiers(toTierRows(data.rateTiers))
      showToast('Sozlamalar saqlandi')
    },
  })

  if (settings.isLoading) {
    return <SettingsSkeleton />
  }

  if (settings.error && !settings.data) {
    return <SettingsRetryState error={settings.error} onRetry={() => void settings.refetch()} />
  }

  if (!settings.data || !draft || !tiers) {
    // TypeScript uchun qo'riqchi — amalda yuqoridagi ikkita shartdan biri doim ushlaydi.
    return null
  }

  const original = settings.data

  // ── Bosqichli stavka: tekshirish + o'zgarganini aniqlash ──
  const tiersEval = evaluateRateTiers(tiers)
  const tiersChanged =
    tiersEval.value !== null &&
    JSON.stringify(tiersEval.value) !== JSON.stringify(normalizeTiers(original.rateTiers))

  // ── Har bir maydonni tozalash + tekshirish (`validation.ts`dagi `evaluateField()`) ──
  const threshold = evaluateField(draft.autoPayoutThreshold, validateAutoPayoutThreshold)
  const payoutDay = evaluateField(draft.scheduledPayoutDay, validateScheduledPayoutDay)
  const firstDays = evaluateField(draft.firstCheckDays, validateFirstCheckDays)
  const minFollowers = evaluateField(draft.minFollowers, validateMinFollowers)
  const reapplyDays = evaluateField(draft.reapplyAfterDays, validateReapplyAfterDays)
  const linkWait = evaluateField(draft.linkWaitDays, validateLinkWaitDays)

  const anyFieldError = [
    tiersEval.error,
    threshold.error,
    payoutDay.error,
    firstDays.error,
    minFollowers.error,
    reapplyDays.error,
    linkWait.error,
  ].some((error) => error !== null)

  // ── Faqat O'ZGARGAN maydonlar PATCH tanasiga qo'shiladi (vazifa talabi #1) ──
  const patchBody: SettingsUpdateRequest = {}
  if (tiersChanged && tiersEval.value !== null) patchBody.rateTiers = tiersEval.value
  if (threshold.value !== null && threshold.value !== original.autoPayoutThreshold) {
    patchBody.autoPayoutThreshold = threshold.value
  }
  if (payoutDay.value !== null && payoutDay.value !== original.scheduledPayoutDay) {
    patchBody.scheduledPayoutDay = payoutDay.value
  }
  if (firstDays.value !== null && firstDays.value !== original.firstCheckDays) {
    patchBody.firstCheckDays = firstDays.value
  }
  if (minFollowers.value !== null && minFollowers.value !== original.minFollowers) {
    patchBody.minFollowers = minFollowers.value
  }
  if (reapplyDays.value !== null && reapplyDays.value !== original.reapplyAfterDays) {
    patchBody.reapplyAfterDays = reapplyDays.value
  }
  if (linkWait.value !== null && linkWait.value !== original.linkWaitDays) {
    patchBody.linkWaitDays = linkWait.value
  }
  if (draft.viewsProvider !== original.viewsProvider) {
    patchBody.viewsProvider = draft.viewsProvider
  }

  const hasChanges = Object.keys(patchBody).length > 0
  const permissionsLoading = admins.isLoading
  // Yozish huquqi yo'q deb SAFE DEFAULT bilan hisoblanadi: ruxsat hali tekshirilayotganda
  // ham, super emasligi aniq bo'lganda ham maydonlar o'qish rejimida qoladi
  // (`hooks.ts`dagi `useCurrentAdmin()` izohi — `isKnown: false` ATAYLAB "yo'q" deb o'qiladi).
  const readOnly = permissionsLoading || !currentAdmin.isSuper
  const canSave = !readOnly && !anyFieldError && hasChanges && !updateMutation.isPending

  function bind(key: keyof Draft) {
    return {
      value: draft![key],
      onChange: (value: string) => {
        // Foydalanuvchi shu maydonga ANIQ qo'l tekkizdi — yuqoridagi effekt endi buni
        // fon yangilanishida qayta yozmaydi (touched to'plami).
        touchedRef.current.add(key)
        setDraft((prev) => (prev ? { ...prev, [key]: value } : prev))
      },
    }
  }

  function handleTierChange(index: number, field: keyof TierRow, value: string) {
    tiersTouchedRef.current = true
    setTiers((prev) =>
      prev ? prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)) : prev,
    )
  }

  function handleAddTier() {
    tiersTouchedRef.current = true
    setTiers((prev) => (prev ? [...prev, { views: '', ratePer1000: '' }] : prev))
  }

  function handleRemoveTier(index: number) {
    tiersTouchedRef.current = true
    setTiers((prev) => (prev && prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))
  }

  function handleSave() {
    if (!canSave) return
    updateMutation.mutate(patchBody)
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Settings size={20} aria-hidden="true" /> Sozlamalar
        </h1>
      </header>

      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
        {settings.error && (
          <SettingsErrorBanner
            error={settings.error}
            isFetching={settings.isFetching}
            onRetry={() => void settings.refetch()}
          />
        )}

        {!permissionsLoading && !currentAdmin.isSuper && (
          <p className="rounded-card border border-app-border bg-app-card px-3 py-2 text-xs text-app-hint">
            Sozlamalarni faqat super admin o&apos;zgartira oladi. Quyidagi qiymatlar faqat
            o&apos;qish uchun ko&apos;rsatilmoqda.
          </p>
        )}

        <PayoutSection
          tiers={tiers}
          onTierChange={handleTierChange}
          onAddTier={handleAddTier}
          onRemoveTier={handleRemoveTier}
          tiersError={tiersEval.error}
          tiersChanged={tiersChanged}
          autoPayoutThreshold={{ ...bind('autoPayoutThreshold'), error: threshold.error }}
          scheduledPayoutDay={{ ...bind('scheduledPayoutDay'), error: payoutDay.error }}
          disabled={readOnly}
        />

        <CheckScheduleSection
          firstCheckDays={{ ...bind('firstCheckDays'), error: firstDays.error }}
          disabled={readOnly}
        />

        <BloggerRulesSection
          minFollowers={{ ...bind('minFollowers'), error: minFollowers.error }}
          reapplyAfterDays={{ ...bind('reapplyAfterDays'), error: reapplyDays.error }}
          linkWaitDays={{ ...bind('linkWaitDays'), error: linkWait.error }}
          disabled={readOnly}
        />

        <AdminsSection currentAdmin={currentAdmin} />

        <ViewsSourceSection
          value={draft.viewsProvider}
          onChange={(value) => {
            touchedRef.current.add('viewsProvider')
            setDraft((prev) => (prev ? { ...prev, viewsProvider: value } : prev))
          }}
          disabled={readOnly}
        />

        {updateMutation.error && (
          <p className="text-sm font-medium text-stage-rejected-text">
            {updateMutation.error.message}
          </p>
        )}

        <Button className="self-end" onClick={handleSave} disabled={!canSave}>
          {updateMutation.isPending && (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          )}
          <Save size={16} aria-hidden="true" />
          Saqlash
        </Button>

        {/* Chiqish — faqat brauzerda (Telegram ichida sessiya cookie'si yo'q). Sidebar'dan
            shu yerga ko'chirildi: tasodifan bosilmasligi uchun tasdiq oynasi bilan. */}
        {!isInsideTelegram() && <LogoutSection />}
      </div>

      <ToastViewport message={toast?.message ?? null} />
    </div>
  )
}

function SettingsSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Settings size={20} aria-hidden="true" /> Sozlamalar
        </h1>
      </header>
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-40" />
        ))}
      </div>
    </div>
  )
}
